"use client"
import styled from 'styled-components';
import { MockBrowser, BrowserContent } from './primitives'

const FIN_QUARTERS = [
  { q: 'Q1', rev: 22.1 },
  { q: 'Q2', rev: 26.0 },
  { q: 'Q3', rev: 35.1 },
  { q: 'Q4', rev: 44.1 },
];

const FIN_METRICS = [
  { label: 'Gross Margin', value: '73.5%', sub: 'Q4 FY24' },
  { label: 'Net Income', value: '$19.3B', sub: '+769% YoY' },
  { label: 'EPS Diluted', value: '$7.70', sub: 'Q4 FY24' },
  { label: 'Revenue Growth', value: '+122%', sub: 'Full year' },
  { label: 'Operating Margin', value: '62.1%', sub: 'Q4 FY24' },
  { label: 'Free Cash Flow', value: '$11.2B', sub: 'Q4 FY24' },
];

const FIN_SEGMENTS = [
  { label: 'Data Center', value: '$35.6B', pct: 100 },
  { label: 'Gaming', value: '$6.4B', pct: 18 },
  { label: 'Professional Visualization', value: '$1.1B', pct: 3 },
  { label: 'Automotive', value: '$0.6B', pct: 1.7 },
  { label: 'OEM & Other', value: '$0.4B', pct: 1.1 },
];

const FinVHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;
const FinVTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111;
`;
const FinVSub = styled.div`
  font-size: 10px;
  color: #aaa;
  margin-top: 2px;
`;
const SourcePill = styled.div`
  font-size: 10px;
  color: #888;
  background: #f5f5f5;
  padding: 3px 8px;
  border-radius: 4px;
  flex-shrink: 0;
`;
const BarChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 14px;
`;
const BarItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;
const BarValLabel = styled.div`
  font-size: 9.5px;
  color: #888;
  font-variant-numeric: tabular-nums;
`;
const Bar = styled.div`
  width: 100%;
  height: ${({ $pct }) => Math.round($pct * 0.8)}px;
  background: linear-gradient(to top, #111 0%, #444 100%);
  border-radius: 3px 3px 0 0;
  min-height: 4px;
`;
const BarQLabel = styled.div`
  font-size: 10px;
  color: #aaa;
`;
const FinMetrics = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;
const FinMetricCard = styled.div`
  background: #f8f8f8;
  border-radius: 8px;
  padding: 10px 12px;

  @media (max-width: 968px) {
    padding: 8px 10px;
  }
`;
const FinMetricLabel = styled.div`
  font-size: 10px;
  color: #888;
  margin-bottom: 4px;
`;
const FinMetricValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  @media (max-width: 400px) {
    font-size: 15px;
  }
`;
const FinMetricSub = styled.div`
  font-size: 10px;
  color: #aaa;
  margin-top: 3px;
`;
const SegmentSection = styled.div`
  margin-top: 16px;
`;
const SegmentHeader = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #111;
  margin-bottom: 10px;
`;
const SegmentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const SegmentLabel = styled.div`
  font-size: 10.5px;
  color: #555;
  width: 130px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 400px) {
    width: 84px;
    font-size: 9.5px;
  }
`;
const SegmentTrack = styled.div`
  flex: 1;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;
const SegmentFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(to right, #444 0%, #111 100%);
  border-radius: 3px;
`;
const SegmentValue = styled.div`
  font-size: 10.5px;
  color: #333;
  font-variant-numeric: tabular-nums;
  width: 48px;
  flex-shrink: 0;
  text-align: right;
`;

export const FinancialsVisual = () => (
  <MockBrowser $path={'/product_images/4.webp'}>
    <BrowserContent>
      <FinVHeader>
        <div>
          <FinVTitle>Revenue · FY 2024</FinVTitle>
          <FinVSub>NVIDIA Corporation · USD Billions</FinVSub>
        </div>
        <SourcePill>10-K · Feb 2025</SourcePill>
      </FinVHeader>

      <BarChart>
        {FIN_QUARTERS.map((q) => (
          <BarItem key={q.q}>
            <BarValLabel>${q.rev}B</BarValLabel>
            <Bar $pct={(q.rev / 44.1) * 100} />
            <BarQLabel>{q.q}</BarQLabel>
          </BarItem>
        ))}
      </BarChart>

      <FinMetrics>
        {FIN_METRICS.map((m) => (
          <FinMetricCard key={m.label}>
            <FinMetricLabel>{m.label}</FinMetricLabel>
            <FinMetricValue>{m.value}</FinMetricValue>
            <FinMetricSub>{m.sub}</FinMetricSub>
          </FinMetricCard>
        ))}
      </FinMetrics>

      <SegmentSection>
        <SegmentHeader>Revenue by Segment · Q4 FY24</SegmentHeader>
        {FIN_SEGMENTS.map((s) => (
          <SegmentRow key={s.label}>
            <SegmentLabel>{s.label}</SegmentLabel>
            <SegmentTrack>
              <SegmentFill $pct={s.pct} />
            </SegmentTrack>
            <SegmentValue>{s.value}</SegmentValue>
          </SegmentRow>
        ))}
      </SegmentSection>
    </BrowserContent>
  </MockBrowser>
);
